import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  getNotifications,
  addNotification,
  updateNotification,
  toggleNotification,
  deleteNotification,
} from "../../services/notificationService";

const toastCfg = { autoClose: 1800 };

const ICON_URL = "https://admin.opiniongames.net/assets/images/fav.png";

/* ─── Styles ─── */
const s = {
  root:        { padding: "20px 24px 48px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },
  pageTitle:   { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub:     { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" },
  card:        { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 28 },
  cardHeader:  { padding: "14px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc", fontWeight: 800, color: "#0f172a", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: 8 },
  cardBody:    { padding: "20px" },
  label:       { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input:       { width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", marginBottom: 14, boxSizing: "border-box" },
  textarea:    { width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", marginBottom: 14, boxSizing: "border-box", resize: "vertical", minHeight: 80 },
  row2:        { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  saveBtn:     { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 28px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer" },
  cancelBtn:   { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "11px 22px", background: "#f1f5f9", border: "1.5px solid #e2e8f0", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#475569", cursor: "pointer" },
  btnRow:      { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  filterRow:   { display: "flex", alignItems: "flex-end", gap: 14, marginBottom: 20, flexWrap: "wrap" },
  filterInput: { padding: "9px 13px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.85rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit" },
  filterBtn:   { padding: "9px 18px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, color: "#fff", cursor: "pointer" },
  clearBtn:    { padding: "9px 14px", background: "#f1f5f9", border: "1.5px solid #e2e8f0", borderRadius: 9, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, color: "#64748b", cursor: "pointer" },
  table:       { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th:          { padding: "11px 14px", background: "#f8fafc", fontWeight: 700, color: "#64748b", textAlign: "left", borderBottom: "1.5px solid #e8edf2", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em" },
  td:          { padding: "12px 14px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "middle" },
  badge:       { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 800, textTransform: "uppercase" },
  badgeOn:     { background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0" },
  badgeOff:    { background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca" },
  iconImg:     { width: 28, height: 28, borderRadius: 6, objectFit: "contain", background: "#f8fafc", border: "1px solid #e8edf2" },
  actionBtn:   { padding: "5px 10px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 },
  editBtn:     { background: "#eff6ff", color: "#3b82f6" },
  toggleBtn:   { background: "#fef9c3", color: "#ca8a04" },
  deleteBtn:   { background: "#fef2f2", color: "#ef4444" },
  emptyRow:    { textAlign: "center", padding: "40px 20px", color: "#94a3b8", fontWeight: 600 },
};

const emptyForm = { title: "", short_description: "", onclick_link: "", is_active: 1 };

export const NotificationListLayer = () => {
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId]   = useState(null); // null = add mode

  /* form state */
  const [form, setForm]         = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  /* date filter */
  const [from, setFrom] = useState("");
  const [to, setTo]     = useState("");

  useEffect(() => { load(); }, []);

  const load = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await getNotifications(filters);
      if (res?.status) setList(res.data ?? []);
      else toast.error(res?.msg || "Failed to load", toastCfg);
    } catch {
      toast.error("Network error", toastCfg);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    const f = {};
    if (from) f.from = from;
    if (to)   f.to   = to;
    load(f);
  };

  const clearFilter = () => {
    setFrom(""); setTo(""); load();
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (n) => {
    setForm({
      title:             n.title,
      short_description: n.short_description,
      onclick_link:      n.onclick_link ?? "",
      is_active:         n.is_active,
    });
    setEditingId(n.id);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.short_description.trim()) {
      toast.error("Title and description are required", toastCfg);
      return;
    }
    setSaving(true);
    try {
      const res = editingId
        ? await updateNotification({ ...form, id: editingId })
        : await addNotification(form);

      if (res?.status) {
        toast.success(editingId ? "Updated" : "Notification created", toastCfg);
        cancelForm();
        load();
      } else {
        toast.error(res?.msg || "Save failed", toastCfg);
      }
    } catch {
      toast.error("Network error", toastCfg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleNotification(id);
      if (res?.status) {
        setList((prev) => prev.map((n) => n.id === id ? { ...n, is_active: n.is_active ? 0 : 1 } : n));
      } else {
        toast.error(res?.msg || "Toggle failed", toastCfg);
      }
    } catch {
      toast.error("Network error", toastCfg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    setDeletingId(id);
    try {
      const res = await deleteNotification(id);
      if (res?.status) {
        toast.success("Deleted", toastCfg);
        setList((prev) => prev.filter((n) => n.id !== id));
      } else {
        toast.error(res?.msg || "Delete failed", toastCfg);
      }
    } catch {
      toast.error("Network error", toastCfg);
    } finally {
      setDeletingId(null);
    }
  };

  const field = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div style={s.root}>
      {/* ── Page Header ── */}
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Notifications</h4>
          <p style={s.pageSub}>Manage push notifications shown to all users</p>
        </div>
        {!showForm && (
          <button style={s.saveBtn} onClick={openAdd}>
            <Icon icon="solar:add-circle-bold" fontSize={16} /> Add Notification
          </button>
        )}
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <Icon icon="solar:bell-bold-duotone" fontSize={18} />
            {editingId ? "Edit Notification" : "New Notification"}
          </div>
          <div style={s.cardBody}>
            <label style={s.label}>Title *</label>
            <input style={s.input} value={form.title} onChange={field("title")} placeholder="e.g. Match Result Announced" />

            <label style={s.label}>Short Description *</label>
            <textarea style={s.textarea} value={form.short_description} onChange={field("short_description")} placeholder="Brief message shown under the title" />

            <div style={s.row2}>
              <div>
                <label style={s.label}>On-Click Link (optional)</label>
                <input style={s.input} value={form.onclick_link} onChange={field("onclick_link")} placeholder="https://... or /page-path" />
              </div>
              <div>
                <label style={s.label}>Status</label>
                <select
                  style={s.input}
                  value={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: parseInt(e.target.value) }))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            {/* Icon preview */}
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Notification Icon (fixed)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={ICON_URL} alt="icon" style={s.iconImg} />
                <span style={{ fontSize: "0.78rem", color: "#64748b" }}>{ICON_URL}</span>
              </div>
            </div>

            <div style={s.btnRow}>
              <button style={s.cancelBtn} onClick={cancelForm} disabled={saving}>Cancel</button>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.65 : 1 }} onClick={handleSave} disabled={saving}>
                {saving
                  ? <><Icon icon="svg-spinners:ring-resize" fontSize={15} /> Saving…</>
                  : <><Icon icon="solar:check-circle-bold" fontSize={15} /> {editingId ? "Save Changes" : "Create"}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter Row ── */}
      <div style={s.filterRow}>
        <div>
          <label style={{ ...s.label, marginBottom: 4 }}>From Date</label>
          <input type="date" style={s.filterInput} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label style={{ ...s.label, marginBottom: 4 }}>To Date</label>
          <input type="date" style={s.filterInput} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button style={s.filterBtn} onClick={applyFilter}>
          <Icon icon="solar:filter-bold" fontSize={14} style={{ marginRight: 4 }} />Filter
        </button>
        {(from || to) && (
          <button style={s.clearBtn} onClick={clearFilter}>Clear</button>
        )}
      </div>

      {/* ── Table ── */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <Icon icon="solar:bell-bing-bold-duotone" fontSize={18} />
          All Notifications
          <span style={{ marginLeft: "auto", fontWeight: 600, fontSize: "0.8rem", color: "#64748b" }}>
            {list.length} record{list.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8" }}>
              <Icon icon="svg-spinners:ring-resize" fontSize={28} />
            </div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>Icon</th>
                  <th style={s.th}>Title</th>
                  <th style={s.th}>Description</th>
                  <th style={s.th}>On-Click Link</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Created At</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={8} style={s.emptyRow}>No notifications found</td></tr>
                ) : (
                  list.map((n, idx) => (
                    <tr key={n.id}>
                      <td style={s.td}>{idx + 1}</td>
                      <td style={s.td}>
                        <img src={n.icon || ICON_URL} alt="icon" style={s.iconImg} />
                      </td>
                      <td style={{ ...s.td, fontWeight: 700, maxWidth: 200 }}>{n.title}</td>
                      <td style={{ ...s.td, color: "#475569", maxWidth: 260 }}>
                        <span title={n.short_description}>
                          {n.short_description.length > 80
                            ? n.short_description.slice(0, 80) + "…"
                            : n.short_description}
                        </span>
                      </td>
                      <td style={{ ...s.td, maxWidth: 180 }}>
                        {n.onclick_link
                          ? <a href={n.onclick_link} target="_blank" rel="noreferrer"
                              style={{ color: "#3b82f6", fontSize: "0.8rem", wordBreak: "break-all" }}>
                              {n.onclick_link.length > 40 ? n.onclick_link.slice(0, 40) + "…" : n.onclick_link}
                            </a>
                          : <span style={{ color: "#cbd5e1" }}>—</span>
                        }
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, ...(n.is_active ? s.badgeOn : s.badgeOff) }}>
                          {n.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ ...s.td, whiteSpace: "nowrap", color: "#64748b", fontSize: "0.8rem" }}>
                        {new Date(n.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                      </td>
                      <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={{ ...s.actionBtn, ...s.editBtn }} onClick={() => openEdit(n)}>
                            <Icon icon="solar:pen-bold" fontSize={13} /> Edit
                          </button>
                          <button style={{ ...s.actionBtn, ...s.toggleBtn }} onClick={() => handleToggle(n.id)}>
                            <Icon icon="solar:power-bold" fontSize={13} />
                            {n.is_active ? "Disable" : "Enable"}
                          </button>
                          <button
                            style={{ ...s.actionBtn, ...s.deleteBtn, opacity: deletingId === n.id ? 0.6 : 1 }}
                            onClick={() => handleDelete(n.id)}
                            disabled={deletingId === n.id}
                          >
                            <Icon icon="solar:trash-bin-trash-bold" fontSize={13} />
                            {deletingId === n.id ? "…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
